import { useParams, NavLink, generatePath, Outlet } from 'react-router-dom';
import { IconArrowLeft, IconLoader, Popup, Textarea } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearBeneficiaryState } from '../store/slices/beneficiaries';
import { OrganizationType } from '../types';
import useBeneficiary from './hooks/use-beneficiary';
import BeneficairyContent from '../../../components/common/beneficiary/beneficairy';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const BeneficiaryPage = ({ type }: { type: OrganizationType }) => {
  const {
    beneficiary,
    beneficiaryContracts,
    documentsCategories,
    beneficiaryContractsErrorsList,
  } = useAppSelector((state) => state.customerBeneficiaries);
  const { baseCurrency, beneficiarySettings } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const {
    currencies: currenciesList,
    countriesList: countries,
    activePermissions,
  } = useAppSelector((state) => state.root);

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const { customerId, beneficiaryId } = useParams();

  const dispatch = useAppDispatch();

  const {
    isLoading,
    isFetching,
    errors,
    statusInProgress,
    beneficiaryStatus,
    onSaveStatus,
    updateStatusError,
    setBeneficiaryStatus,
    setRejectPopupVisible,
    globalValidateError,
    contractsFormattedData,
    filters,
    contractsIsLoading,
    contractsIsFetching,
    formattedDocumentsData,
    documentsCategoriesIsLoading,
    documentsCategoriesIsFetching,
    onRowClick,
    rejectPopupVisible,
    setRejectComment,
    rejectComment,
    setRejectCommentError,
    rejectCommentError,
  } = useBeneficiary({
    beneficiaryId,
    beneficiary,
    documentsCategories,
    beneficiaryContracts,
    type,
    customerId,
    baseCurrency,
    currenciesList,
  });

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.beneficiaryLoader}>
        <IconLoader />
      </div>
    );
  } else if (beneficiary) {
    content = (
      <BeneficairyContent
        beneficiary={beneficiary}
        errors={errors}
        organizationStatus={organizationStatus}
        statusInProgress={statusInProgress}
        beneficiaryStatus={beneficiaryStatus}
        onSaveStatus={onSaveStatus}
        updateStatusError={updateStatusError}
        setBeneficiaryStatus={setBeneficiaryStatus}
        setRejectPopupVisible={setRejectPopupVisible}
        activePermissions={activePermissions}
        countries={countries}
        globalValidateError={globalValidateError}
        beneficiarySettings={beneficiarySettings}
        contractsFormattedData={contractsFormattedData}
        filters={filters}
        contractsIsLoading={contractsIsLoading}
        contractsIsFetching={contractsIsFetching}
        beneficiaryContracts={beneficiaryContracts}
        beneficiaryContractsErrorsList={beneficiaryContractsErrorsList}
        formattedDocumentsData={formattedDocumentsData}
        documentsCategoriesIsLoading={documentsCategoriesIsLoading}
        documentsCategoriesIsFetching={documentsCategoriesIsFetching}
        onRowClick={onRowClick}
      />
    );
  }

  return (
    <>
      <div className={cn.beneficiaryTop}>
        <NavLink
          to={generatePath(
            type === OrganizationType.INDIVIDUAL
              ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARIES
              : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES,
            { customerId: customerId as string },
          )}
          className={cn.backLink}
          onClick={() => dispatch(clearBeneficiaryState())}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </NavLink>
      </div>
      {content}
      <Popup
        isVisible={rejectPopupVisible}
        onCancelClick={() => {
          setBeneficiaryStatus(beneficiary?.status);
          setRejectPopupVisible(false);
          setRejectComment('');
        }}
        onOkClick={() => {
          if (!rejectComment) {
            setRejectCommentError('Description of the reason is required');
          } else {
            setRejectCommentError('');
            onSaveStatus();
          }
        }}
        isLoading={statusInProgress}
        okButtonText={statusInProgress ? 'Loading...' : 'Save'}
        cancelButtonText="Cancel"
        title="Describe the reason for the rejection"
      >
        <Textarea
          name="rejectComment"
          value={rejectComment}
          onChange={(e) => setRejectComment(e.target.value)}
          placeholder="Describe the reason..."
          error={rejectCommentError}
          labelClassName={cn.rejectCommentTextarea}
        />
      </Popup>
      <Outlet />
    </>
  );
};

export default BeneficiaryPage;
